import { MapPin, Loader2, Crosshair, Building2, RefreshCw, BarChart3, Megaphone, Bug, Database } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';
import Segmented from '../../components/settings/Segmented';
import Toggle from '../../components/settings/Toggle';
import AutomationPanel from '../../components/settings/AutomationPanel';
import { useSettingsStore } from '../../store/settingsStore';
import type { LocationSettings } from '../../types/settings.types';
import { cn } from '../../utils/helpers';

export default function Location() {
  const { data, loading, saveLocation } = useSettingsStore();

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const loc = data.location;
  const update = (patch: Partial<LocationSettings>) => saveLocation(patch);

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">Location & Services</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Control permissions and how your data is used</p>
      </div>

      <SettingCard>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-neutral-900 text-white flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
            <div>
              <p className="font-bold">Location access</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Allow Messego to use your location</p>
            </div>
          </div>
          <Toggle checked={loc.locationEnabled} onChange={(v) => update({ locationEnabled: v })} label="Location access" />
        </div>
      </SettingCard>

      <div className={cn('transition-opacity', !loc.locationEnabled && 'opacity-40 pointer-events-none')}>
        <SettingCard title="Precision & sharing" flush>
          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Crosshair className="w-5 h-5" /></div>
              <div><p className="font-semibold text-sm">Precision</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Precise pinpoints you exactly</p></div>
            </div>
            <Segmented
              value={loc.precision}
              onChange={(precision) => update({ precision })}
              options={[{ value: 'precise', label: 'Precise' }, { value: 'approximate', label: 'Approx.' }]}
            />
          </div>
          <RowToggle icon={Building2} label="Share with businesses" description="Let stores personalize offers by area" checked={loc.shareWithBusinesses} onChange={(v) => update({ shareWithBusinesses: v })} />
          <RowToggle icon={RefreshCw} label="Background refresh" description="Update location while app is closed" checked={loc.backgroundRefresh} onChange={(v) => update({ backgroundRefresh: v })} />
        </SettingCard>
      </div>

      <SettingCard title="Data & privacy" flush>
        <SettingsRow icon={Database} label="Data usage" description="See what Messego uses and manage limits" to="/app/settings/location/data-usage" />
        <RowToggle icon={BarChart3} label="Usage analytics" description="Help improve Messego with anonymous stats" checked={loc.analyticsEnabled} onChange={(v) => update({ analyticsEnabled: v })} />
        <RowToggle icon={Megaphone} label="Personalized ads" description="Use activity to tailor advertising" checked={loc.personalizedAds} onChange={(v) => update({ personalizedAds: v })} />
        <RowToggle icon={Bug} label="Crash reports" description="Automatically send diagnostics on errors" checked={loc.crashReports} onChange={(v) => update({ crashReports: v })} />
      </SettingCard>

      <AutomationPanel category="data" title="Data automation" description="AI optimizes data usage on the fly" />
    </div>
  );
}

function RowToggle({
  icon: Icon,
  label,
  description,
  checked,
  onChange,
}: {
  icon: typeof MapPin;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <div className="flex items-center gap-3.5">
        <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Icon className="w-5 h-5" /></div>
        <div><p className="font-semibold text-sm">{label}</p><p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p></div>
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </div>
  );
}
